import firebase from 'src/utils/firebase';
import { getActiveShopId } from 'src/utils/shopContext';
import { markStartup, measureStartup } from 'src/utils/startupPerformance';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { socketClient } from '../index';


const initialState = {
  connectionStatus: '',
  connectedShopId: null,
  targetShopId: null,
  connectionRequestId: null,
  users: [],
};

export const connectToSocket = createAsyncThunk('connectToSocket', async function ({ shopId } = {}) {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    throw new Error('Cannot connect the realtime channel before authentication.');
  }
  const targetShopId = Number(shopId || getActiveShopId());
  markStartup('socket-connect-start');
  await socketClient.connect(await currentUser.getIdToken(), targetShopId);
  markStartup('socket-connected');
  measureStartup('socket-connect', 'socket-connect-start', 'socket-connected');
  return { shopId: targetShopId };
});

export const disconnectFromSocket = createAsyncThunk('disconnectFromSocket', async function () {
  await socketClient.disconnect();
}, {
  condition: (_, { getState }) => {
    const status = getState().socket.connectionStatus;
    return status !== 'disconnecting' && status !== 'disconnected';
  }
});

const slice = createSlice({
  name: 'socket',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(connectToSocket.pending, (state, action) => {
      state.connectionStatus = 'connecting';
      state.targetShopId = Number(action.meta.arg?.shopId || 0) || null;
      state.connectionRequestId = action.meta.requestId;
    });
    builder.addCase(connectToSocket.fulfilled, (state, action) => {
      if (state.connectionRequestId !== action.meta.requestId) return;
      state.connectionStatus = 'connected';
      state.connectedShopId = Number(action.payload?.shopId || 0) || null;
      state.targetShopId = state.connectedShopId;
      state.connectionRequestId = null;
    });
    builder.addCase(connectToSocket.rejected, (state, action) => {
      if (state.connectionRequestId !== action.meta.requestId) return;
      state.connectionStatus = 'connection failed';
      state.connectedShopId = null;
      state.connectionRequestId = null;
    });
    builder.addCase(disconnectFromSocket.pending, (state) => {
      state.connectionStatus = 'disconnecting';
      state.connectionRequestId = null;
    });
    builder.addCase(disconnectFromSocket.fulfilled, (state) => {
      state.connectionStatus = 'disconnected';
      state.connectedShopId = null;
      state.targetShopId = null;
      state.connectionRequestId = null;
    });
    builder.addCase(disconnectFromSocket.rejected, (state) => {
      state.connectionStatus = 'disconnection failed';
      state.connectedShopId = null;
    });
  },
});

export const reducer = slice.reducer;
